/**
 * The data seam. The UI calls api.* only; the adapter talks to the Pages Functions (remote) or to
 * browser storage (local demo mode). Both keep the S.tables cache in sync so views stay synchronous.
 */
import { ID_PREFIX } from '../shared/constants';
import type { Row, Tables } from '../shared/types';
import { nowISO, uid } from '../shared/util';
import { downscale } from './images';
import { S, T, byId, setTables } from './state';
import { toast } from './ui';

export interface Adapter {
  bootstrap(): Promise<{ me: Row | null; household: Row | null; tables: Tables; config?: Record<string, any>; signedOut?: boolean }>;
  create(table: string, row: Row): Promise<Row>;
  update(table: string, id: string, patch: Row): Promise<Row | undefined>;
  remove(table: string, id: string): Promise<void>;
  setSetting(key: string, value: any): Promise<void>;
  putPhoto(big: { blob: Blob; w: number; h: number }, small: { blob: Blob; w: number; h: number }, meta: Row): Promise<Row>;
  deletePhoto(p: Row): Promise<void>;
  deleteItem(id: string): Promise<void>;
  enrich(itemId: string, force?: boolean): Promise<any>;
  runJob(jobId: string): Promise<any>;
  publicSale(slug: string): Promise<any>;
  reserve(slug: string, body: Row): Promise<any>;
  checkout(slug: string, saleItemId: string): Promise<{ url?: string; error?: string }>;
  authRequest(email: string, turnstileToken?: string): Promise<any>;
  authCode(email: string, code: string): Promise<any>;
  logout(all: boolean): Promise<void>;
  inviteUser(user: Row | { email: string; role: string }): Promise<any>;
  backup(): Promise<Tables>;
  tick?(): Promise<any>;
}

class ApiError extends Error { constructor(public status: number, msg: string) { super(msg); } }
async function call<T = any>(method: string, path: string, body?: any, form?: FormData): Promise<T> {
  const res = await fetch(path, { method, credentials: 'same-origin', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: form || (body ? JSON.stringify(body) : undefined) });
  if (res.status === 401) { S.ui.signedOut = true; throw new ApiError(401, 'Please sign in'); }
  const text = await res.text(); let data: any = null; try { data = text ? JSON.parse(text) : null; } catch { data = { error: text }; }
  if (!res.ok) throw new ApiError(res.status, data?.error || `HTTP ${res.status}`);
  return data;
}

function localAudit(entity: string, entityId: string, field: string, oldv: any, newv: any) {
  T('audit_log').unshift({ id: uid('au'), household_id: S.me?.household_id, entity, entity_id: entityId, field, old_value: oldv == null ? null : String(oldv), new_value: newv == null ? null : String(newv), user_id: S.me?.id || null, at: nowISO() });
}

export const RemoteAdapter: Adapter = {
  async bootstrap() { try { return await call('GET', '/api/bootstrap'); } catch (e: any) { if (e.status === 401) return { me: null, household: null, tables: {}, signedOut: true }; throw e; } },
  async create(table, row) { const saved = table === 'users' ? (await call('POST', '/api/users', row)).user : await call('POST', `/api/rows/${table}`, row); T(table).push(saved); localAudit(table, saved.id, '*', null, 'created'); return saved; },
  async update(table, id, patch) {
    const r = byId(table, id); if (!r) return;
    const before = { ...r }; Object.assign(r, patch); // optimistic
    try { const saved = await call('PATCH', table === 'users' ? `/api/users/${id}` : `/api/rows/${table}/${id}`, patch); Object.assign(r, saved); for (const [k, v] of Object.entries(patch)) if (String(before[k] ?? '') !== String(v ?? '')) localAudit(table, id, k, before[k], v); return r; }
    catch (e) { Object.assign(r, before); throw e; }
  },
  async remove(table, id) { await call('DELETE', `/api/rows/${table}/${id}`); (S.tables as any)[table] = T(table).filter(r => r.id !== id); },
  async setSetting(key, value) { await call('POST', '/api/settings', { key, value }); const r = T('settings').find(s => s.key === key); if (r) r.value = JSON.stringify(value); else T('settings').push({ id: uid('st'), key, value: JSON.stringify(value) }); },
  async putPhoto(big, small, meta) { const fd = new FormData(); fd.append('file', big.blob, 'photo.jpg'); fd.append('thumb', small.blob, 'thumb.jpg'); fd.append('meta', JSON.stringify({ ...meta, width: big.w, height: big.h })); const row = await call('POST', '/api/photos', undefined, fd); T('photos').push(row); return row; },
  async deletePhoto(p) { await call('DELETE', `/api/photos/${p.id}`); S.tables.photos = T('photos').filter(x => x.id !== p.id); },
  async deleteItem(id) { await call('DELETE', `/api/items/${id}`); S.tables.items = T('items').filter(x => x.id !== id); S.tables.photos = T('photos').filter(x => x.item_id !== id); },
  async enrich(itemId, force) { const job = await call('POST', `/api/items/${itemId}/enrich${force ? '?force=1' : ''}`); T('ai_jobs').push(job); const it = byId('items', itemId); if (it) it.status = 'enriching'; schedulePoll(); return job; },
  async runJob(jobId) { const job = await call('POST', `/api/ai-jobs/${jobId}/run`); Object.assign(byId('ai_jobs', jobId) || {}, job); schedulePoll(); return job; },
  publicSale: slug => call('GET', `/api/public/sale/${encodeURIComponent(slug)}`),
  reserve: (slug, body) => call('POST', `/api/public/sale/${encodeURIComponent(slug)}/reserve`, body),
  async checkout(slug, saleItemId) { try { return await call('POST', `/api/public/sale/${encodeURIComponent(slug)}/checkout`, { sale_item_id: saleItemId }); } catch (e: any) { return { error: e.message }; } },
  authRequest: (email, turnstileToken) => call('POST', '/api/auth/request', { email, turnstileToken }),
  authCode: (email, code) => call('POST', '/api/auth/verify-code', { email, code }),
  async logout(all) { await call('POST', `/api/auth/logout${all ? '?all=1' : ''}`); },
  async inviteUser(user: any) { if (user.id) return call('POST', `/api/users/${user.id}/invite`); const r = await call('POST', '/api/users', user); T('users').push(r.user); return r.invite; },
  backup: () => call('GET', '/api/export/backup'),
  tick: () => call('POST', '/api/admin/tick'),
};

let pollTimer: any = null, polls = 0;
/** While items are enriching, refresh the cache every few seconds (production jobs finish in the queue). */
export function schedulePoll() {
  if (S.mode !== 'remote') return;
  clearTimeout(pollTimer); polls = 0;
  const step = async () => {
    if (!T('items').some(i => i.status === 'enriching') || polls++ > 40) return;
    try { await api.refresh(); } catch {}
    pollTimer = setTimeout(step, 4000);
  };
  pollTimer = setTimeout(step, 3000);
}

export const api = {
  adapter: RemoteAdapter as Adapter,
  async bootstrap() {
    const b = await this.adapter.bootstrap();
    if (b.signedOut) { S.ui.signedOut = true; S.me = null; setTables({}); return; }
    setTables(b.tables || {}); S.me = b.me; S.household = b.household; S.config = b.config || {}; S.ui.signedOut = false;
  },
  async refresh() { const b = await this.adapter.bootstrap(); if (b.signedOut) { S.ui.signedOut = true; return; } setTables(b.tables || {}); S.me = b.me || S.me; const { render } = await import('./nav'); render(); },
  create(table: string, row: Row) { return this.adapter.create(table, { id: row.id || uid(ID_PREFIX[table] || table.slice(0, 3)), ...row }); },
  update(table: string, id: string, patch: Row) { return this.adapter.update(table, id, patch); },
  remove(table: string, id: string) { return this.adapter.remove(table, id); },
  setSetting(key: string, value: any) { return this.adapter.setSetting(key, value); },
  putPhoto(big: any, small: any, meta: Row) { return this.adapter.putPhoto(big, small, meta); },
  async photoFromFile(file: File, meta: Row) { const big = await downscale(file, 1600); const small = await downscale(file, 400, 0.75); return this.putPhoto(big, small, meta); },
  deletePhoto(p: Row) { return this.adapter.deletePhoto(p); },
  deleteItem(id: string) { return this.adapter.deleteItem(id); },
  async enrich(itemId: string, force = false) { try { return await this.adapter.enrich(itemId, force); } catch (e: any) { toast('AI request failed: ' + e.message); } },
  runJob(jobId: string) { return this.adapter.runJob(jobId); },
  publicSale(slug: string) { return this.adapter.publicSale(slug); },
  reserve(slug: string, body: Row) { return this.adapter.reserve(slug, body); },
  checkout(slug: string, id: string) { return this.adapter.checkout(slug, id); },
  authRequest(email: string, tok?: string) { return this.adapter.authRequest(email, tok); },
  authCode(email: string, code: string) { return this.adapter.authCode(email, code); },
  logout(all: boolean) { return this.adapter.logout(all); },
  inviteUser(u: any) { return this.adapter.inviteUser(u); },
  backup() { return this.adapter.backup(); },
  tick() { return this.adapter.tick?.(); },
};
